import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {DomainSetting} from '@gatewee/types/domain-setting';
import {RecipientSetting} from '@gatewee/types/recipient-setting';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeInputV2Component} from '../../gatewee-input-v2/gatewee-input-v2.component';

interface DialogData {
  id: number;
  setting: RecipientSetting | DomainSetting;
  editSettingFn: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
}

@Component({
  selector: 'fee-batch-minimum-parameter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeInputV2Component,
  ],
  templateUrl: './fee-batch-minimum-parameter.component.html',
})
export class FeeBatchMinimumParameterComponent implements OnInit {
  formGroup!: FormGroup;
  submitting = false;
  id!: number;
  setting!: RecipientSetting | DomainSetting;
  editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    const data = this.context.data;
    this.id = data.id;
    this.setting = data.setting;
    this.editSettingFn = data.editSettingFn;
  }

  ngOnInit(): void {
    const raw = this.setting.value;
    let str = '';
    if (raw !== undefined && raw !== null) {
      str =
        typeof raw === 'string' || typeof raw === 'number'
          ? String(raw)
          : JSON.stringify(raw);
    }
    this.formGroup = this.fb.group({
      value: [str, Validators.required],
    });
  }

  onSubmit(): void {
    if (this.formGroup.invalid || this.submitting) {
      this.formGroup.markAllAsTouched();
      return;
    }
    this.submitting = true;
    const v = String(this.formGroup.get('value')?.value ?? '').trim();
    const normalized = v.replace(',', '.');
    const updatedSetting: RecipientSetting | DomainSetting = {
      ...this.setting,
      value: normalized,
    };
    this.editSettingFn(this.id, updatedSetting).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateSuccess', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            },
          )
          .subscribe();
        this.context.completeWith(true);
      },
      error: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateError', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close(): void {
    this.context.completeWith(false);
  }
}
